/**
 * Zone and stage helpers for the pipeline UI.
 *
 * `humanizeSlug` and `getZoneSummary` previously existed as byte-identical
 * copies in PipelineDetailPage and PipelineDashboard. That drift is what let
 * the dashboard get an overflow cap ("+N more") while the detail page kept
 * rendering every zone name into one unclamped paragraph. They live here now
 * so a fix lands in both places at once.
 */
import type { PipelineConfig, PipelineStep, StepStatus } from '../services/pipelineOrchestrator';

/**
 * Human labels for `step_order`. The orchestrator emits a fixed six-stage
 * pipeline; the collection stage (1) is the only one that fans out, with one
 * step per zone x collector.
 */
export const STAGE_LABELS: Record<number, string> = {
  1: 'Collection',
  2: 'Reconciliation',
  3: 'Deduplication',
  4: 'VIN Processing',
  5: 'Post Processing',
  6: 'Notification'
};

/** `ford-lfd-albion` -> `Ford Lfd Albion`. */
export function humanizeSlug(slug: string): string {
  return slug.replace(/[-_]/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

/** Groups a run's configured zones into `zone_slug -> collectors[]`. */
export function getZoneSummary(config?: PipelineConfig): Map<string, string[]> | null {
  if (!config?.zones?.length) return null;
  const grouped = new Map<string, string[]>();
  for (const z of config.zones) {
    const existing = grouped.get(z.zone_slug) ?? [];
    existing.push(z.collector);
    grouped.set(z.zone_slug, existing);
  }
  return grouped;
}

/**
 * `PipelineStep.config` is an untyped `Record<string, unknown>` on the wire, so
 * every read needs narrowing rather than a cast.
 */
function readString(config: Record<string, unknown> | undefined, key: string): string | undefined {
  const value = config?.[key];
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

/** The zone a step belongs to. Only collection steps carry one. */
export function getStepZone(step: PipelineStep): string | undefined {
  return readString(step.config, 'zone_slug');
}

/** The collector a step used. Only collection steps carry one. */
export function getStepCollector(step: PipelineStep): string | undefined {
  return readString(step.config, 'collector');
}

export type StepStatusCounts = Record<StepStatus, number>;

export interface PipelineStage {
  order: number;
  label: string;
  steps: PipelineStep[];
  total: number;
  counts: StepStatusCounts;
  /** Rolled-up status for the stage as a whole. */
  status: StepStatus;
  /** Terminal steps (completed, skipped, cancelled) — the numerator of progress. */
  settled: number;
}

function emptyCounts(): StepStatusCounts {
  return {
    pending: 0,
    running: 0,
    completed: 0,
    failed: 0,
    skipped: 0,
    cancelled: 0,
    partial_success: 0
  };
}

/**
 * Rolls a stage's steps up to one status.
 *
 * Precedence is deliberate: anything still running dominates, because an
 * in-flight stage is the thing an operator most needs to see. A stage that has
 * both failures and successes reports `partial_success` rather than hiding
 * either half.
 */
function rollUpStatus(counts: StepStatusCounts, total: number): StepStatus {
  if (counts.running > 0) return 'running';
  if (counts.pending === total) return 'pending';

  const succeeded = counts.completed + counts.skipped;
  const broken = counts.failed + counts.partial_success;

  if (broken > 0) return succeeded > 0 ? 'partial_success' : 'failed';
  if (counts.cancelled > 0 && succeeded === 0) return 'cancelled';
  if (counts.pending > 0) return 'running'; // partly done, nothing actively running
  return 'completed';
}

/**
 * Groups steps into ordered stages by `step_order`.
 *
 * This is what lets the detail page show six rows instead of 188 nodes: the
 * fan-out is summarised as counts here and only expanded in the steps table.
 */
export function groupStepsByStage(steps: PipelineStep[]): PipelineStage[] {
  if (!steps?.length) return [];

  const byOrder = new Map<number, PipelineStep[]>();
  for (const step of steps) {
    const existing = byOrder.get(step.step_order) ?? [];
    existing.push(step);
    byOrder.set(step.step_order, existing);
  }

  return Array.from(byOrder.keys())
    .sort((a, b) => a - b)
    .map(order => {
      const stageSteps = byOrder.get(order) ?? [];
      const counts = emptyCounts();
      for (const step of stageSteps) {
        if (step.status in counts) counts[step.status] += 1;
      }
      const total = stageSteps.length;
      return {
        order,
        label: STAGE_LABELS[order] ?? `Stage ${order}`,
        steps: stageSteps,
        total,
        counts,
        status: rollUpStatus(counts, total),
        settled: counts.completed + counts.skipped + counts.cancelled
      };
    });
}
